import AssetService from './AssetService';
import BrackenUser from '../models/User';
import TokenService from './TokenService';
import { User } from 'oidc-client';
import _ from 'lodash';
import axios from 'axios';
import dayjs from 'dayjs';

const CHECK_TOKEN_INTERVAL = 60000; // 1 minute
class AuthService {
  private currentUser: User | null;
  private brackenUser: BrackenUser;
  /** API Key (e.g. sharing linkkey) for authorization when not logged in. */
  private apiKey: string;

  private get ClientID(): string {
    // Client ID is tenant's domain e.g. ttg.portal.bracken.cloud
    const host = window.location.host;
    const clientID =
      host.indexOf(':') > 0
        ? host.substring(0, host.indexOf(':')) // removing :<port> for local development
        : host;

    return clientID;
  }

  public get Tenant(): string {
    return this.ClientID.substr(0, this.ClientID.indexOf('.'));
  }

  public get User() {
    return this.currentUser;
  }

  public get BrackenUser() {
    return this.brackenUser;
  }
  public set BrackenUser(user: BrackenUser) {
    this.brackenUser = user;
  }

  public get Userkey(): number {
    return +_.get(this, 'currentUser.profile.sub');
  }

  public get Username() {
    return _.get(this, 'currentUser.profile.name') as string;
  }

  public constructor() {
    this.currentUser = null;
    this.startTokenRefreshTimer();
  }

  private startTokenRefreshTimer() {
    window.setInterval(async () => {
      console.debug(
        `Token expires in ${this.currentUser?.expires_at - dayjs().unix()}`
      );
      if (this.currentUser?.expires_at - dayjs().unix() < 120) {
        try {
          await this.refreshTokenAsync();
        } catch (error) {
          this.login();
        }
      }
    }, CHECK_TOKEN_INTERVAL);
  }

  public getTopParentZoneId() {
    return +_.get(this, 'currentUser.profile.topparentzoneid');
  }

  public getAccessToken() {
    return this.currentUser?.access_token;
  }

  public getUser() {
    return this.currentUser;
  }

  public getUserkey() {
    return +this.currentUser.profile.sub;
  }
  
  /** Set the API key. */
  public setApiKey(key: string) {
    this.apiKey = key;
  }

  /** Return the API key. */
  public get ApiKey() {
    return this.apiKey;
  }

  /**
   * Logs the user in by creating a login form and posting it to the webpack
   * running server, which authenticates via Identity Server (using Passport).
   */
  public async login(): Promise<void> {
    console.log('Logging in...');
    const form = document.createElement('form');
    form.action = `/auth/login`;
    form.method = 'GET';

    /** Add a parameter to the form to be submitted. */
    const addFormParam = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    // Specified identity provider.
    const params = new URLSearchParams(document.location.search);
    const idp = params.get('idp');
    if (idp) {
      addFormParam('idp', idp);
    }

    // Custom landing page, other than the dashboard.
    const target = document.location.pathname;
    if (target && target !== '/') {
      addFormParam('target', `${target}${document.location.search}`);
    }

    // Submit form to start auth process.
    document.body.appendChild(form);
    form.submit();
  }

  public async logout() {
    window.location.assign(
      `/auth/logout?id_token=${this.currentUser?.id_token}`
    );
  }

  public async isAuthenticatedAsync() {
    try {
      if (
        await TokenService.verifyAccessTokenAsync(
          this.currentUser?.access_token
        )
      ) {
        return this.currentUser;
      }
      await this.refreshTokenAsync();

      return this.currentUser;
    } catch (error) {
      this.currentUser = null;
      throw error;
    }
  }

  private async refreshTokenAsync() {
    try {
      const { data } = await axios.get<Oidc.User>('/auth/refresh');
      this.currentUser = data;
      AssetService.getContainerSas();
    } catch (error) {
      throw error;
    }
  }

  public isLoggedIn(username: string): boolean {
    return this.currentUser?.profile?.name === username;
  }
}

export default new AuthService();
